import React, {useState} from "react";
import type {TextFieldProps} from '@mui/material/TextField';
import {Alert, Box, Dialog, DialogActions, DialogContentText, IconButton, TextField} from "@mui/material";
import type {GridFilterInputValueProps} from '@mui/x-data-grid';
import {useGridRootProps} from '@mui/x-data-grid';
import {Sync} from "@mui/icons-material";
import FilterIcon from "~/components/icons/svg/FilterIcon.svg?react";
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';

const HTML_ID_PREFIX = 'integer-between-filter-';

/** Allows an optional leading minus sign followed by digits, so negative values (and "-" mid-typing) work. */
function sanitizeIntegerInput(raw: string): string {
    return raw.match(/^-?\d*/)?.[0] ?? '';
}

/**
 * Unlike ScoreBetweenFilter (which has defaults substituted in
 * CategorizationReviewPageFilterController.apiGridFilterModel for missing bounds), this filter
 * has no such defaulting, and the BE can't handle a "between" filter value like [null, "1"] - so
 * both bounds are required here, not just "if set, must be valid".
 */
function hasExactlyOneBound(lowerBound: string | undefined, upperBound: string | undefined): boolean {
    return (lowerBound !== undefined) !== (upperBound !== undefined);
}

function validateInput(lowerBound: string | undefined, upperBound: string | undefined): boolean {
    if (lowerBound === undefined || upperBound === undefined) return false;
    const lower = Number(lowerBound);
    const upper = Number(upperBound);
    if (Number.isNaN(lower) || Number.isNaN(upper)) return false;
    return lower <= upper;
}

/**
 * General-purpose integer "between" filter input, unlike ScoreBetweenFilterComponent this has no
 * pre-configured 0..100 range: 0 and negative values are valid, and there is no upper bound.
 */
export const IntegerBetweenFilterComponent: React.FC<GridFilterInputValueProps> = ({
                                                                                       item,
                                                                                       applyValue,
                                                                                       focusElementRef = null
                                                                                   }) => {
    // Taken from https://mui.com/x/react-data-grid/filtering/customization/#multiple-values-operator

    const rootProps = useGridRootProps();

    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const handleClear = () => {
        setFilterValueState([undefined, undefined]);
        setIsDialogOpen(false);
    }
    const handleClose = () => setIsDialogOpen(false);
    const handleSave = () => {
        if (Array.isArray(filterValueState)) {
            const [lowerBound, upperBound] = filterValueState;
            setIsApplying(true);
            filterTimeout.current = setTimeout(() => {
                setIsApplying(false);
                applyValue({...item, value: [lowerBound, upperBound]});
            }, rootProps.filterDebounceMs);
        }
        setIsDialogOpen(false);
    };

    const filterTimeout = React.useRef<ReturnType<typeof setTimeout>>(undefined);
    const [filterValueState, setFilterValueState] = React.useState<[string | undefined, string | undefined]>(item.value ?? [undefined, undefined]);
    const [applying, setIsApplying] = React.useState(false);
    const filterValueStateIsSet = Array.isArray(filterValueState) && filterValueState.length === 2 && (Boolean(filterValueState[0]) || Boolean(filterValueState[1]));

    React.useEffect(() => {
        return () => {
            clearTimeout(filterTimeout.current);
        };
    }, []);

    React.useEffect(() => {
        const itemValue = item.value ?? [undefined, undefined];
        setFilterValueState(itemValue);
    }, [item.value]);

    const updateFilterValue = (lowerBound: string | undefined, upperBound: string | undefined) => {
        clearTimeout(filterTimeout.current);
        setFilterValueState([lowerBound, upperBound]);
    };
    const handleLowerFilterChange: TextFieldProps['onChange'] = (event) => {
        const sanitized = sanitizeIntegerInput(event.target.value);
        updateFilterValue(sanitized === '' ? undefined : sanitized, filterValueState[1]);
    };
    const handleUpperFilterChange: TextFieldProps['onChange'] = (event) => {
        const sanitized = sanitizeIntegerInput(event.target.value);
        updateFilterValue(filterValueState[0], sanitized === '' ? undefined : sanitized);
    };

    const isActive = applying || filterValueStateIsSet;

    return <>
        <Dialog open={isDialogOpen}
                onClose={handleClose}
                maxWidth="sm"
                fullWidth
                aria-labelledby={HTML_ID_PREFIX + "-title"}
                aria-describedby={HTML_ID_PREFIX + "-description"}>
            <DialogTitle id={HTML_ID_PREFIX + "-title"}>
                Filter by value
            </DialogTitle>
            <DialogContent>
                <DialogContentText id={HTML_ID_PREFIX + "-description"} component="div">
                    <Box
                        sx={{
                            display: 'inline-flex',
                            flexDirection: 'row',
                            alignItems: 'end',
                            height: 48,
                            pl: '20px',
                        }}
                    >
                        <TextField
                            name="lower-bound-input"
                            placeholder="From"
                            label="From"
                            variant="standard"
                            value={filterValueState[0] ?? ''}
                            onChange={handleLowerFilterChange}
                            inputRef={focusElementRef}
                            sx={{mr: 2}}
                        />
                        <TextField
                            name="upper-bound-input"
                            placeholder="To"
                            label="To"
                            variant="standard"
                            value={filterValueState[1] ?? ''}
                            onChange={handleUpperFilterChange}
                            InputProps={applying ? {endAdornment: <Sync/>} : {}}
                        />
                    </Box>
                    {hasExactlyOneBound(filterValueState[0], filterValueState[1]) && <Alert
                        severity="info"
                        className="m-4"
                    >
                        This filter requires both From and To. If you only need one bound, use the
                        "Greater than" / "Less than" operators instead.
                    </Alert>}
                    {!hasExactlyOneBound(filterValueState[0], filterValueState[1]) &&
                        !validateInput(filterValueState[0], filterValueState[1]) &&
                        (filterValueState[0] !== undefined || filterValueState[1] !== undefined) && <Alert
                            severity="warning"
                            className="m-4"
                    >
                            Invalid filter! Make sure From&nbsp;&le;&nbsp;To
                    </Alert>}
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClear}>Clear</Button>
                <Button onClick={handleClose}>Cancel</Button>
                <Button onClick={handleSave} autoFocus
                        disabled={!validateInput(filterValueState[0], filterValueState[1])}>
                    Confirm
                </Button>
            </DialogActions>
        </Dialog>
        <Box sx={{display: 'flex', justifyContent: 'center', width: '100%'}}>
            <IconButton
                size="small" color={isActive ? "primary" : "default"}
                onClick={() => setIsDialogOpen(true)}
            >
                <FilterIcon fontSize="small" color="primary"/>
            </IconButton>
        </Box>
    </>;

}
